import { useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faDownload, faImage } from '@fortawesome/free-solid-svg-icons';
import { useAiImageTypesGeneration } from '../../hooks/useAiImageTypesGeneration';
import { aiStateTypes} from '../../types/ai/states';
import { ImageGernerationSimpleInputProps, ImageGernerationSimpleProps } from '../../types/imageSimple/imageGeneration';
import { getImagePixelSize } from '../../utils/images/imageConvertFormats';
import {  ImageGenerationBodyProps, samplerList } from '../../types/ai/api';
import { handleGetRandomNumber } from '../../utils/calculations';
import { styleDescriptionPrompt } from '../../utils/aiModels/prompts';
import { downloadOriginal } from '../../utils/exportMedia';
import { AuthedImage } from '../../components/ui/AuthedImage';
import { usePromptHook } from '../../hooks/usePromptHook';
import { parsePromptResponse } from '../../utils/chat/chatPropsParsing';
import { PromptArea } from '../../components/app/generation/PromptArea';
import { SettingAIOptions } from '../../components/app/generation/SettingAIOptions';
import { useIsMobile } from '../../hooks/useIsMobile';
import { StatePreview } from '../../components/app/generation/StatePreview';
import { useUpdateImageGenerationCosts } from '../../hooks/useUpdateImageGenerationCosts';
import { FilePlaceHolderWithDragAndDrop } from '../../components/fileupload/filePlaceholderWithDragAndDrop';
import { useNavigateBack } from '../../hooks/useNavigationBack';

export const ImageGeneration = () => {
    const { t } = useTranslation(['generation','toolsApps','apps','imageStudio']);
    const isMobile = useIsMobile();
    const navigateBack = useNavigateBack();
    const [generatedImages,setGeneratedImages] = useState<ImageGernerationSimpleProps[]>([]);
    const [lastGeneration,setLastGeneration] = useState<{src:string, id:number}|null>();
    const [creationCosts, updateCosts] = useUpdateImageGenerationCosts();
    const [seedRandomActive, setSeedRandomActive] = useState(true);
    const [generation,state,setState,getJobID,insufficientCreditsDialog] = useAiImageTypesGeneration();
    const [generationLLM,stateLLM,setStateLLM,handleRandomPromptExternal,handleCheckPromptExternal, _,insufficientCreditsDialogLLM] = usePromptHook();
    const [aiGenerationIsRunning, setAiGenerationIsRunning] = useState(false);
    const [aiState, setAiState] = useState<aiStateTypes>(null);
    const [inputs, setInputs] = useState<ImageGernerationSimpleInputProps>({
        promptNegative:'',
        promptPositive: '',
        samplingSteps: 12,
        cfg: 2.5,
        sampler: samplerList.length > 0 ? samplerList[0] : 'Euler',
        seed: 42,
        scheduler: 'simple',
        style: 'none',
        quality: 'Standard',
        aspectRatio: '1:1 (quadratic)',
        numberOgGeneration: 1,
        reference: null
    });

    const handleGenerationResult = () => {
        setAiGenerationIsRunning(false);
        let img = generation;
        //add image to history list
        const newGeneratedImages:ImageGernerationSimpleProps[] = [...generatedImages];
        let lastID = newGeneratedImages.length > 0 ? newGeneratedImages[newGeneratedImages.length-1].id : 0;
        newGeneratedImages.push({
            'id':lastID+1,
            'promptPositive': inputs.promptPositive,
            'promptNegative': inputs.promptNegative,
            'src': img
        });
        setLastGeneration({
            src: img[0],
            id: lastID +1
        });
        setGeneratedImages(newGeneratedImages);
        if(seedRandomActive){
            const currentInputs = {...inputs};
            currentInputs.seed = handleGetRandomNumber();
            setInputs(currentInputs);
        }
    };
    const generateImage = async() => {
        setState(null);
        setAiGenerationIsRunning(true);
        //request to get job id
        const imageDimension = getImagePixelSize(inputs.aspectRatio, inputs.quality ?? 'standard');
        const prompt = styleDescriptionPrompt(inputs.promptPositive,inputs.promptNegative, inputs.style, false);
        const body: ImageGenerationBodyProps = {
            positive_prompt: prompt.positive_prompt, 
            negative_prompt: prompt.negative_prompt ? prompt.negative_prompt : undefined,
            width: imageDimension.width,
            height: imageDimension.height,
            seed: inputs.seed,
            steps: inputs.samplingSteps,
            scheduler: inputs.scheduler,
            model: 'Qwen_Image',
            cfg: inputs.cfg,
            number_of_images: 1,
            sampler: inputs.sampler
        };
        setState('send');
        let path = 'generate/image'
        try {
            getJobID('image_gen',path,body)
        } catch(err){
            setState('error');
            setAiGenerationIsRunning(false);
        };
        
    };
    const handleRandomPrompt = async () => {
        setState(null);
        setAiGenerationIsRunning(true);
        await handleRandomPromptExternal('create',true); 
    };
    const handleCheckPrompt = async () => {
        setState(null);
        setAiGenerationIsRunning(true);
        await handleCheckPromptExternal(inputs.promptPositive,inputs.promptNegative, 'create');
    };
    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputs(prev => ({...prev,[name]: value}));
    };
    const downloadImages = (imageID:number) => {
        const imgObject: ImageGernerationSimpleProps | undefined = generatedImages.find(item => item.id === imageID);
        if(imgObject === undefined){return;}
        for(let imgUrl of imgObject.src){
            const fileName = imgUrl.replaceAll('\\','/').split('/').slice(-1)[0];
            downloadOriginal(imgUrl, fileName).catch(err => console.error('Download fehlgeschlagen', err));
        }
    };
    

    useEffect(()=>{
        document.title = t('apps:registry.app.imageGeneration.name')
    },[]);

    useEffect(()=>{
        if(generation.length > 0){
            handleGenerationResult();
        }
    },[generation]);
    useEffect(()=>{
        updateCosts(inputs);
    },[inputs]);
    useEffect(() => {
        setAiGenerationIsRunning(false);
        setStateLLM(null);
        if (!generationLLM) return;
        const promptResponse = parsePromptResponse(generationLLM);
        if (!promptResponse) return;


        setInputs(prev => ({
            ...prev,
            prompt_positive: promptResponse.positive_prompt,
            prompt_negative: promptResponse.negative_prompt ?? ""
        }));
    }, [generationLLM]);

    useEffect(()=>{
        if(state === 'done' || state === 'error' || state === 'unavailable' || stateLLM === 'error' || stateLLM === 'unavailable' || stateLLM === 'done'){
            setAiGenerationIsRunning(false);
        }
        if(state !== null){
            setStateLLM(null);
            setAiState(state);
        }
        if(stateLLM !== null){
            setState(null);
            setAiState(stateLLM);
        }
        if(!state && !stateLLM){
            setAiState(null);
        }
    },[state,stateLLM]);
  return (
    <>
        {insufficientCreditsDialog}
        {insufficientCreditsDialogLLM}
        <div className="container-fluid h-100 pb-2">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0 align-items-center'>
                    <div className='col-auto p-2 pe-0 align-items-center'>
                        <button 
                            onClick={() => navigateBack()}
                            className="btn btn-outline-secondary btn-sm ms-3"
                            title={t('toolsApps:back')}
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </button>
                    </div>
                    <div className='col'/>
                    <div className='col-auto h3 align-items-center'>
                        {t('generateImage')}
                    </div>
                    <div className='col'/>
                </div>
                <StatePreview state={aiState} />
                <div className={`row flex-grow-1 overflow-hidden p-2 ${!isMobile && generatedImages.length === 0 ? "justify-content-center" : ""}`}>
                    <div className={`${!isMobile && generatedImages.length > 0 ? "col-6" : isMobile ? "col-12" : "col-8"} h-100 p-0`}>
                        <div className="position-relative h-100 w-100 d-flex align-items-center justify-content-center overflow-hidden">
                            <FilePlaceHolderWithDragAndDrop 
                                imageSrc={ lastGeneration ? lastGeneration.src : null}
                                imageAltText={t('lastGeneratedImage')}
                                onDownload={() => downloadImages(lastGeneration ? lastGeneration.id : 0)}
                                donwloadTitle={t("download.image")}
                                placeholderIcon={faImage}
                                placeholderText={t('noGeneratedImage')}
                            />
                        </div>
                    </div>
                    {! isMobile &&(
                        <div className='col-6 h-100 px-2'>
                            {generatedImages.slice().sort((a, b) => b.id - a.id).map(item=>(
                                <div className='row py-2 text-center align-items-center border-bottom border-secondary'>
                                    <div className='col-5 d-flex flex-wrap py-auto' style={{gap:'10px'}}>
                                        {item.src.map(img => (
                                            <AuthedImage
                                                src={img}
                                                alt={`Image ${item.id}`}
                                                className={'rounded d-block'}
                                                style={{height:'200px', width: 'auto'}}
                                            />
                                        ))
                                        }
                                    </div>
                                    <div className='col d-flex flex-column justify-content-center text-start'>
                                        <div className='row mb-2'>
                                            <p className='fw-bold'>{t('prompt.positiveShort')}</p>
                                            <p className='mb-2'>{item.promptPositive}</p>
                                            <p className='fw-bold'>{t('prompt.negativeShort')}</p>
                                            <p className='mb-2'>{item.promptNegative}</p>
                                        </div>
                                        <div className='row'>
                                            <div className='col-auto p-0'>
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    title={t('download.image')}
                                                    onClick={() => downloadImages(item.id)}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faDownload}
                                                        size='xl'
                                                        className='bg-transparent me-2'
                                                    />
                                                    {t('download.image')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <PromptArea
                    functionsDisabled={aiGenerationIsRunning}
                    credits={creationCosts ?? 0}
                    onCheckPrompt={handleCheckPrompt}
                    onRandomPrompt={handleRandomPrompt}
                    onGenerateImage={generateImage}
                    promptNegative={inputs.promptNegative ?? ''}
                    promptPositive={inputs.promptPositive ?? ''}
                    onInputChange={handleInputChange}
                />
                
                <SettingAIOptions
                    aspectRatio={inputs.aspectRatio}
                    style={inputs.style}
                    sampler={inputs.sampler}
                    samplingSteps={inputs.samplingSteps}
                    seed={inputs.seed}
                    seedRandomActive={seedRandomActive}
                    setSeedRandomActive={setSeedRandomActive}
                    cfg={inputs.cfg}
                    scheduler={inputs.scheduler}
                    quality={inputs.quality}
                    onInputsChange={setInputs}
                />
            </div>
        </div>
    </>
  );
};

export default ImageGeneration;